import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import styles from '../styles/Header.module.css';
import logo from '../assets/logo.webp';
import tiktokIcon from '../assets/icons/tiktok-icon.webp';
import facebookIcon from '../assets/icons/facebook-icon.webp';
import instagramIcon from '../assets/icons/instagram-icon.webp';
import twitterIcon from '../assets/icons/twitter-icon.webp';
import telegramIcon from '../assets/icons/telegram-icon.webp';
import AuthModal from './AuthModal';
import { useAccount } from '../context/AccountContext';
import { ADMIN_PATH } from '../config/adminRoutes';

const Header = () => {
    const { user, loading, logout } = useAccount();
    const location = useLocation();
    const [authOpen, setAuthOpen] = React.useState(false);
    // En movil el nav no se apila siempre a la vista (ocupaba media
    // pantalla antes de ver ningun contenido) -- se oculta por defecto y se
    // despliega como un panel al pulsar el boton hamburguesa. En escritorio
    // este estado no tiene efecto: el CSS solo lo usa por debajo de 860px.
    const [menuOpen, setMenuOpen] = React.useState(false);
    const menuToggleRef = React.useRef(null);
    const closeMenu = () => setMenuOpen(false);
    const navClassName = ({ isActive }) => isActive ? styles.navLinkActive : undefined;
    const handleAlertsClick = (event) => {
        closeMenu();
        if (location.pathname !== '/' || location.hash !== '#alertas') return;
        const section = document.getElementById('alertas');
        if (!section) return;
        event.preventDefault();
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    React.useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname, location.search, location.hash]);

    // Con el menu abierto, el fondo no debe poder desplazarse detras del
    // overlay -- si no, se queda una sensacion rara de "menu flotando sobre
    // contenido que se mueve". Se restaura siempre al cerrar/desmontar.
    React.useEffect(() => {
        if (!menuOpen) {
            return undefined;
        }
        const previousOverflow = document.body.style.overflow;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setMenuOpen(false);
                menuToggleRef.current?.focus();
            }
        };
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [menuOpen]);

    return (
        <header className={styles.header}>
            <div className={styles.logo}>
                <Link to="/" className={styles.brandLink} aria-label="Esganga inicio">
                    <span className={styles.brandMark}>
                        <img src={logo} alt="Esganga" />
                    </span>
                </Link>
                <button
                    ref={menuToggleRef}
                    type="button"
                    className={styles.menuToggle}
                    aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
                    aria-expanded={menuOpen}
                    aria-controls="primary-navigation"
                    onClick={() => setMenuOpen((current) => !current)}
                >
                    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                        {menuOpen ? (
                            <path fill="currentColor" d="M6.4 19 5 17.6l5.6-5.6L5 6.4 6.4 5l5.6 5.6L17.6 5 19 6.4 13.4 12l5.6 5.6-1.4 1.4-5.6-5.6Z" />
                        ) : (
                            <path fill="currentColor" d="M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z" />
                        )}
                    </svg>
                </button>
            </div>
            {menuOpen && <div className={styles.navOverlay} onClick={closeMenu} aria-hidden="true" />}
            <nav id="primary-navigation" aria-label="Navegación principal" className={`${styles.nav} ${menuOpen ? styles.navOpen : ''}`}>
                <ul>
                    <li><NavLink to="/buscar" className={navClassName} onClick={closeMenu}>Gangas</NavLink></li>
                    <li><NavLink to="/cupones" className={navClassName} onClick={closeMenu}>Cupones</NavLink></li>
                    <li><NavLink to="/comparar" className={navClassName} onClick={closeMenu}>Comparar</NavLink></li>
                    <li><NavLink to="/buscador-inteligente" className={navClassName} onClick={closeMenu}>¿Qué necesitas?</NavLink></li>
                    <li><NavLink to="/tiendas" className={navClassName} onClick={closeMenu}>Tiendas</NavLink></li>
                    <li><NavLink to="/juegos" className={navClassName} onClick={closeMenu}>Juegos</NavLink></li>
                    <li><Link to="/#alertas" onClick={handleAlertsClick}>Alertas</Link></li>
                    {user && <li><NavLink to="/mis-alertas" className={navClassName} onClick={closeMenu}>Mis alertas</NavLink></li>}
                    {user && <li><NavLink to="/mis-favoritos" className={navClassName} onClick={closeMenu}>Mis favoritos</NavLink></li>}
                    {user && <li><NavLink to="/vendedor" className={navClassName} onClick={closeMenu}>Publicar</NavLink></li>}
                    {user?.role === 'admin' && <li><a href={ADMIN_PATH} onClick={closeMenu}>Panel</a></li>}
                    <li><NavLink to="/contact" className={navClassName} onClick={closeMenu}>Contacto</NavLink></li>
                </ul>
            </nav>
            <div className={styles.headerActions}>
                {loading
                    ? <span className={styles.loginButtonSkeleton} aria-hidden="true" />
                    : (
                        user
                            ? (
                                <button type="button" className={styles.accountButton} onClick={logout}>
                                    {user.displayName || user.email} · Salir
                                </button>
                            )
                            : (
                                <button type="button" className={styles.loginButton} onClick={() => setAuthOpen(true)}>
                                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                        <path
                                            fill="currentColor"
                                            d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-9 2.24-9 5v2a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-2c0-2.76-4.58-5-9-5Z"
                                        />
                                    </svg>
                                    Iniciar sesión
                                </button>
                            )
                    )}
                {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}
                <Link to="/buscar" className={styles.hotCta}>Buscar ganga</Link>
                <div className={styles['social-icons']}>
                    <a href="https://www.tiktok.com/@esganga.es/" target="_blank" rel="noopener noreferrer"><img src={tiktokIcon} alt="TikTok"/></a>
                    <a href="https://www.facebook.com/www.esganga.es" target="_blank" rel="noopener noreferrer"><img src={facebookIcon} alt="Facebook"/></a>
                    <a href="https://www.instagram.com/esganga_es/" target="_blank" rel="noopener noreferrer"><img src={instagramIcon} alt="Instagram"/></a>
                    <a href="https://twitter.com/esganga_es" target="_blank" rel="noopener noreferrer"><img src={twitterIcon} alt="Twitter"/></a>
                    <a href="https://t.me/esganga" target="_blank" rel="noopener noreferrer"><img src={telegramIcon} alt="Telegram"/></a>
                </div>
            </div>
        </header>
    );
};

export default Header;
